import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronsUpDown, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { searchContracts, type ContractPickerOption } from '../api'

interface Props {
  value: ContractPickerOption | null
  onChange: (v: ContractPickerOption | null) => void
  placeholder?: string
}

/**
 * Searchable picker umowy easycrm.
 * Debounce 300ms, zapytanie tylko gdy popover otwarty.
 * Wynik: numer umowy (mono) + zamawiajacy + etap.
 */
export function ContractPicker({ value, onChange, placeholder = 'Szukaj umowy...' }: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const q = useQuery({
    queryKey: ['contracts', 'search', debouncedSearch],
    queryFn: () => searchContracts(debouncedSearch),
    enabled: open,
    staleTime: 30_000,
  })

  const handleSelect = (option: ContractPickerOption) => {
    onChange(option)
    setOpen(false)
    setSearch('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
  }

  const buttonLabel = value
    ? `${value.code} - ${value.customerName}`
    : placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            !value && 'text-muted-foreground',
          )}
        >
          <span className="truncate">{buttonLabel}</span>
          <span className="flex items-center gap-1">
            {value && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClear(e as unknown as React.MouseEvent)
                }}
                className="rounded p-0.5 hover:bg-muted"
                aria-label="Wyczysc umowe"
              >
                <X className="size-3.5" />
              </span>
            )}
            <ChevronsUpDown className="size-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <div className="border-b p-2">
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj po numerze umowy lub nazwisku..."
            className="h-8"
          />
        </div>
        <ul className="max-h-72 overflow-y-auto p-1">
          {!open || (!debouncedSearch && q.data == null) ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">Wpisz fragment numeru lub nazwiska...</li>
          ) : q.isLoading ? (
            <li className="p-2"><Skeleton className="h-4 w-full" /></li>
          ) : q.isError ? (
            <li className="px-3 py-2 text-sm text-destructive">Blad ladowania wynikow.</li>
          ) : q.data && q.data.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">Brak wynikow.</li>
          ) : (
            q.data?.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={cn(
                    'flex w-full flex-col rounded px-2 py-1.5 text-left hover:bg-muted',
                    option.id === value?.id && 'bg-muted',
                  )}
                >
                  <span className="font-mono text-sm text-sky-700">{option.code}</span>
                  <span className="text-xs text-slate-500">
                    {option.customerName} &middot; {option.stageName}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
